/**
 * useSidebar - Sidebar state management composable
 *
 * CRITICAL DESIGN PATTERN: Module-level state
 *
 * Unlike typical composables that create instance-level state (new ref per component),
 * this composable uses MODULE-LEVEL state defined OUTSIDE the exported function.
 *
 * Result: ALL components calling useSidebar() share the SAME reactive ref.
 *
 * Example:
 *   const sidebar1 = useSidebar() // sidebar1.sidebarHidden = shared ref
 *   const sidebar2 = useSidebar() // sidebar2.sidebarHidden = SAME REF
 *
 * This replaces Pinia/Vuex for simple shared state without the overhead.
 *
 * Additional features:
 *   - localStorage persistence across page refreshes
 *   - Cross-tab synchronization via storage events
 *   - Automatic cleanup with onMounted/onUnmounted lifecycle hooks
 *
 * Original Mezmo implementation: Pinia store (useUIStore)
 * Aura adaptation: Lightweight composable with module-level ref
 */
import { ref, watch, onMounted, onUnmounted } from 'vue'

const STORAGE_KEY = 'aura-sidebar-hidden'

// Module-level state (shared across instances)
const sidebarHidden = ref(
  localStorage.getItem(STORAGE_KEY) === 'true'
)

// Persist to localStorage
watch(sidebarHidden, (hidden) => {
  if (hidden) {
    localStorage.setItem(STORAGE_KEY, 'true')
  } else {
    localStorage.removeItem(STORAGE_KEY)
  }
})

// Cross-tab sync handler
function handleStorageChange(e) {
  if (e.key === STORAGE_KEY) {
    sidebarHidden.value = e.newValue === 'true'
  }
}

export function useSidebar() {
  // Enable cross-tab sync when component mounts
  onMounted(() => {
    window.addEventListener('storage', handleStorageChange)
  })

  onUnmounted(() => {
    window.removeEventListener('storage', handleStorageChange)
  })

  function toggleSidebar() {
    sidebarHidden.value = !sidebarHidden.value
  }

  function setSidebarHidden(hidden) {
    sidebarHidden.value = hidden
  }

  return {
    sidebarHidden,
    toggleSidebar,
    setSidebarHidden
  }
}
